import { useFormContext } from "react-hook-form";
import {
  getFromStatisticsAdsCampaign,
  getLabelCount,
  getDateBasedLabel,
  METRICS_TO_CONVERT_TO_MAIN_CURRENCY,
} from "../../../../services/adsCampaigns";
import { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { getUniqueID } from "../../../../utils/numbers";
import {
  ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA,
  ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPC,
  ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPM,
  ADS_CAMPAIGN_STATISTICS_SLIDE_MENU_STATISTICS,
  ADS_CAMPAIGNS_STATISTICS_PERFORMANCE_TRENDS,
  CLICKS,
  CREATE_CAMPAIGN_AUDIENCE_LOCATION_ALL_OPTION,
  DATE_DAY_AND_MONTH_SHORT,
  IMPRESSIONS,
  PREVIOUS_PERIOD,
  RESULTS,
  TOTAL_CLICKS,
  TOTAL_IMPRESSIONS,
  TOTAL_RESULTS,
  TOTAL_ROAS_GOOGLE,
  TOTAL_ROAS_META,
  TOTAL_ROAS_TIK_TOK,
  TOTAL_SPEND,
  VALUE_NUMBER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { GOOGLE, META, TIKTOK } from "../../../../utils/constants";
import LineChart from "../../../../components/Charts/LineChart";
import useResizeItemsWithSameHeight from "../../../../hooks/useResizeItemsWithSameHeight";
import { numberDecimal } from "../../../../utils/numbers.js";
import StatisticCard from "../../../../components/Statistics/StatisticCard";
import Select from "../../../../components/Form/Select";

const MAP_STATISTICS_COMPARISON_BY_METRIC = {
  cpm: ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPM,
  cpc: ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPC,
  costPerMainAction: ADS_CAMPAIGN_STATISTICS_PLATFORMS_CPA,
  impressions: IMPRESSIONS,
  click: CLICKS,
  mainAction: RESULTS,
};

const grids = {
  xs: 12,
  sm: 6,
  lg: 4,
};

const OverviewStatistics = ({ filteredLogs, isLoadingLogs }) => {
  const { campaign } = useFormContext();
  const { platforms, statistics, previousPeriodStatistics, historical } =
    getFromStatisticsAdsCampaign(campaign);
  const { t } = useTranslationApp();

  return (
    <Box
      id="overview-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
        gap: 3,
        height: "100%",
        pt: 3,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
        }}
      >
        {t(ADS_CAMPAIGN_STATISTICS_SLIDE_MENU_STATISTICS)}
      </Typography>
      <TotalMetrics
        statistics={statistics}
        previousPeriodStatistics={previousPeriodStatistics}
        platforms={platforms}
      />
      <TotalMetricsTrends
        historical={historical}
        previousPeriodStatistics={previousPeriodStatistics}
      />
      <ComparisonBetweenPlatforms
        statistics={statistics}
        historical={historical}
        filteredLogs={filteredLogs}
        isLoadingLogs={isLoadingLogs}
      />
    </Box>
  );
};

const TotalMetrics = ({ statistics, previousPeriodStatistics, platforms }) => {
  const { t } = useTranslationApp();
  const { getCostByMainCurrency } = useFormContext();

  const getTotalSpendAndCurrencyPlatform = (stats) => {
    let totalSpend = 0;
    let totalCurrency = "";

    platforms.forEach((platform) => {
      const { cost, currency } = getCostByMainCurrency({
        cost: stats?.[platform]?.campaign?.spend,
        currency: stats?.[platform]?.campaign?.currency,
      });
      totalSpend += cost;
      totalCurrency = currency;
    });

    return {
      spend: totalSpend,
      currency: totalCurrency,
    };
  };

  const getTotalStatistics = (stats) => {
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalMainActions = 0;

    Object.keys(stats || {}).forEach((platform) => {
      if (platform !== "startDateTime" && platform !== "endDateTime") {
        totalImpressions += stats?.[platform]?.campaign?.impressions || 0;
        totalClicks += stats?.[platform]?.campaign?.click || 0;
        totalMainActions += stats?.[platform]?.campaign?.mainAction || 0;
      }
    });

    return {
      impressions: totalImpressions,
      clicks: totalClicks,
      mainActions: totalMainActions,
    };
  };

  const previousTotals = getTotalStatistics(previousPeriodStatistics);
  const currentTotals = getTotalStatistics(statistics);
  const getGrowthPercentage = (current, previous) => {
    if (
      (previous === 0 || previous === undefined || previous === null) &&
      (current === 0 || current === undefined || current === null)
    )
      return 0;
    if (
      (previous === 0 || previous === undefined || previous === null) &&
      current > 0
    )
      return 100;
    if (previous === undefined || previous === null) return null;
    if (current === undefined || current === null) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  const totalMetrics = [
    {
      value: currentTotals.impressions,
      previousValue: previousTotals.impressions,
      growth: getGrowthPercentage(
        currentTotals.impressions,
        previousTotals.impressions
      ),
      label: t(TOTAL_IMPRESSIONS),
    },
    {
      value: currentTotals.clicks,
      previousValue: previousTotals.clicks,
      growth: getGrowthPercentage(currentTotals.clicks, previousTotals.clicks),
      label: t(TOTAL_CLICKS),
    },
    {
      value: currentTotals.mainActions,
      previousValue: previousTotals.mainActions,
      growth: getGrowthPercentage(
        currentTotals.mainActions,
        previousTotals.mainActions
      ),
      label: t(TOTAL_RESULTS),
    },
  ];

  const currentSpend = getTotalSpendAndCurrencyPlatform(statistics);
  const previousSpend = getTotalSpendAndCurrencyPlatform(
    previousPeriodStatistics
  );
  const spendGrowth = getGrowthPercentage(
    currentSpend.spend,
    previousSpend.spend
  );

  const getRoasLabel = (platform) => {
    if (platform === GOOGLE) return t(TOTAL_ROAS_GOOGLE);
    if (platform === TIKTOK) return t(TOTAL_ROAS_TIK_TOK);
    if (platform === META) return t(TOTAL_ROAS_META);
    return `${platform.toUpperCase()} ROAS`;
  };

  const roasMetrics = platforms.reduce((acc, platform) => {
    const currentRoas = statistics?.[platform]?.campaign?.roas;
    const previousRoas = previousPeriodStatistics?.[platform]?.campaign?.roas;

    if (currentRoas !== undefined) {
      acc.push({
        platform,
        value: currentRoas,
        previousValue: previousRoas,
        growth: getGrowthPercentage(currentRoas, previousRoas),
        label: getRoasLabel(platform),
      });
    }
    return acc;
  }, []);

  const filteredRoasMetrics = roasMetrics.filter(
    (metric) => metric.value !== 0
  );

  const allMetrics = [
    ...totalMetrics,
    {
      value: currentSpend.spend,
      previousValue: previousSpend.spend,
      growth: spendGrowth,
      label: t(TOTAL_SPEND),
      currency: currentSpend.currency,
      previousCurrency: previousSpend.currency,
    },
    ...filteredRoasMetrics,
  ];

  const getResponsiveGridColumns = (metricsCount) => {
    if (metricsCount <= 2) {
      return {
        xs: "repeat(1, 1fr)",
        sm: `repeat(${Math.min(2, metricsCount)}, 1fr)`,
        md: `repeat(${metricsCount}, 1fr)`,
      };
    } else if (metricsCount <= 4) {
      return {
        xs: "repeat(1, 1fr)",
        sm: "repeat(2, 1fr)",
        md: `repeat(${metricsCount}, 1fr)`,
      };
    } else if (metricsCount <= 6) {
      return {
        xs: "repeat(1, 1fr)",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: `repeat(${Math.min(6, metricsCount)}, 1fr)`,
      };
    } else {
      return {
        xs: "repeat(1, 1fr)",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(4, 1fr)",
        xl: "repeat(6, 1fr)",
      };
    }
  };

  const gridColumns = getResponsiveGridColumns(allMetrics.length);

  return (
    <Box
      data-pdf-kpi-row
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: gridColumns.xs,
          sm: gridColumns.sm,
          md: gridColumns.md,
          lg: gridColumns.lg,
          xl: gridColumns.xl,
        },
        gap: 3,
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {allMetrics.map((metric) => (
        <Box
          key={getUniqueID()}
          data-pdf-card
          sx={{
            display: "flex",
            height: "100%",
          }}
        >
          <StatisticCard
            label={metric.label}
            value={metric.value}
            currency={metric.currency}
            hideIcon
            growth={metric.growth}
            previousValue={metric.previousValue}
            previousCurrency={metric.previousCurrency}
            showPreviousPeriod={true}
            previousPeriodLabel={t(PREVIOUS_PERIOD)}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

const TotalMetricsTrends = () => {
  const { t } = useTranslationApp();
  const {
    watch,
    reportConfiguration,
    getAggregatedMetrics,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const startDateTime = new Date(reportConfiguration?.startDateTime);
  const endDateTime = new Date(reportConfiguration?.endDateTime);
  const breakdown = watch("breakdown");

  const [filter, setFilter] = useState("results");
  const trendsFilterValue = watch("trendsFilter");
  const hasSetInitialFilter = useRef(false);

  useEffect(() => {
    if (hasSetInitialFilter.current) return;
    const { current: dataProcessed } = getAggregatedMetrics({
      includePreviousPeriod: false,
    });
    let totalMainActions = 0;
    Object.keys(dataProcessed || {}).forEach((key) => {
      const snapshot = dataProcessed[key] || {};
      Object.keys(snapshot).forEach((platform) => {
        if (platform !== "startDateTime" && platform !== "endDateTime") {
          totalMainActions += snapshot[platform]?.campaign?.mainAction || 0;
        }
      });
    });
    const defaultFilter = totalMainActions > 0 ? "results" : "clicks";
    setValue("trendsFilter", defaultFilter, { shouldValidate: false });
    setFilter(defaultFilter);
    hasSetInitialFilter.current = true;
  }, [getAggregatedMetrics, setValue]);

  useEffect(() => {
    if (trendsFilterValue !== undefined && trendsFilterValue !== filter) {
      setFilter(trendsFilterValue);
    }
  }, [trendsFilterValue, filter]);

  const label = getLabelCount(breakdown);

  const getWeeklyMetrics = () => {
    let chartConfig = {};
    let chartData = [];

    const { current: dataProcessed, previous: previousPeriodData } =
      getAggregatedMetrics({ includePreviousPeriod: true });
    const currentKeys = Object.keys(dataProcessed);
    const previousKeys = previousPeriodData
      ? Object.keys(previousPeriodData)
      : [];
    const maxLength = Math.max(currentKeys.length, previousKeys.length);

    for (let i = 0; i < maxLength; i++) {
      const currentSnapshot = dataProcessed[currentKeys[i]] || {};
      const previousSnapshot = previousPeriodData
        ? previousPeriodData[previousKeys[i]] || {}
        : {};

      let totalImpressions = 0;
      let totalClicks = 0;
      let totalMainActions = 0;
      Object.keys(currentSnapshot).forEach((platform) => {
        if (platform !== "startDateTime" && platform !== "endDateTime") {
          const campaignStats = currentSnapshot[platform]?.campaign;
          if (campaignStats) {
            totalImpressions +=
              numberDecimal(campaignStats.impressions / 1000, 1) || 0;
            totalClicks += campaignStats.click || 0;
            totalMainActions += campaignStats.mainAction || 0;
          }
        }
      });

      let prevImpressions = 0;
      let prevClicks = 0;
      let prevMainActions = 0;
      Object.keys(previousSnapshot).forEach((platform) => {
        if (platform !== "startDateTime" && platform !== "endDateTime") {
          const campaignStats = previousSnapshot[platform]?.campaign;
          if (campaignStats) {
            prevImpressions +=
              numberDecimal(campaignStats.impressions / 1000, 1) || 0;
            prevClicks += campaignStats.click || 0;
            prevMainActions += campaignStats.mainAction || 0;
          }
        }
      });

      const impressionsGrowth =
        prevImpressions > 0
          ? ((totalImpressions - prevImpressions) / prevImpressions) * 100
          : 0;
      const clicksGrowth =
        prevClicks > 0 ? ((totalClicks - prevClicks) / prevClicks) * 100 : 0;
      const mainActionsGrowth =
        prevMainActions > 0
          ? ((totalMainActions - prevMainActions) / prevMainActions) * 100
          : 0;

      const currentStartDateTime =
        currentSnapshot?.startDateTime || previousSnapshot?.startDateTime;
      const currentEndDateTime =
        currentSnapshot?.endDateTime || previousSnapshot?.endDateTime;

      const dateBasedLabel = getDateBasedLabel(
        breakdown,
        currentStartDateTime,
        currentEndDateTime
      );

      chartData.push({
        label: dateBasedLabel || t(label, { value: i + 1 }),
        breakdown,
        labelToolTip: `${t(DATE_DAY_AND_MONTH_SHORT, {
          date: new Date(
            new Date(currentStartDateTime).getTime() + 24 * 60 * 60 * 1000
          ),
        })} - ${t(DATE_DAY_AND_MONTH_SHORT, {
          date: new Date(currentEndDateTime),
        })}`,
        impressions: numberDecimal(totalImpressions),
        impressionsPrevious: numberDecimal(prevImpressions),
        impressionsGrowth: numberDecimal(impressionsGrowth),
        clicks: numberDecimal(totalClicks),
        clicksPrevious: numberDecimal(prevClicks),
        clicksGrowth: numberDecimal(clicksGrowth),
        mainActions: numberDecimal(totalMainActions),
        mainActionsPrevious: numberDecimal(prevMainActions),
        mainActionsGrowth: numberDecimal(mainActionsGrowth),
      });
    }

    const clicksVisible = filter === "all" || filter === "clicks";
    const resultsVisible = filter === "all" || filter === "results";

    chartConfig = {
      clicks: {
        label: t(CLICKS),
        color: "hsl(var(--line-2))",
        hasLabelList: true,
        isVisible: clicksVisible,
        growthFormatter: (value) => `${value}%`,
      },
      clicksPrevious: {
        label: t(PREVIOUS_PERIOD),
        color: "transparent",
        hasLabelList: false,
        isVisible: clicksVisible, // Show previous period when clicks is visible
      },
      mainActions: {
        label: t(RESULTS),
        color: "hsl(var(--line-3))",
        hasLabelList: true,
        isVisible: resultsVisible,
        growthFormatter: (value) => `${value}%`,
      },
      mainActionsPrevious: {
        label: t(PREVIOUS_PERIOD),
        color: "transparent",
        hasLabelList: false,
        isVisible: resultsVisible, // Show previous period when results is visible
      },
    };

    const title = t(ADS_CAMPAIGNS_STATISTICS_PERFORMANCE_TRENDS);
    const subtitle = `${t(DATE_DAY_AND_MONTH_SHORT, {
      date: startDateTime,
    })} - ${t(DATE_DAY_AND_MONTH_SHORT, {
      date: endDateTime,
    })}`;

    // Calculate dynamic domain for YAxis when only one line is visible
    const getYAxisDomain = () => {
      if (filter === "all") {
        return undefined; // Use default [0, "dataMax"]
      }

      const visibleKey = filter === "clicks" ? "clicks" : "mainActions";
      const values = chartData
        .map((data) => {
          const val = data[visibleKey];
          if (val === undefined || val === null) return null;
          const numVal = typeof val === "number" ? val : parseFloat(val);
          return !isNaN(numVal) ? numVal : null;
        })
        .filter((val) => val !== null && val !== undefined && !isNaN(val));

      if (values.length === 0) {
        return undefined;
      }

      const min = Math.min(...values);
      const max = Math.max(...values);

      // If max is 0, return a small range
      if (max === 0) {
        return [0, 1];
      }

      // If all values are the same or very close, add some padding
      if (max === min || (max > 0 && (max - min) / max < 0.01)) {
        const padding = Math.max(max * 0.1, 1);
        return [Math.max(0, min - padding), max + padding];
      }

      const range = max - min;
      const margin = Math.max(range * 0.1, max * 0.05); // At least 5% of max or 10% of range
      const calculatedMin = Math.max(0, min - margin);
      const calculatedMax = max + margin;

      // Return explicit domain to force Recharts to use it
      return [calculatedMin, calculatedMax];
    };

    return {
      chartConfig,
      chartData,
      title,
      subtitle,
      xAxisKey: "label",
      yAxisDomain: getYAxisDomain(),
    };
  };

  const weeklyMetrics = getWeeklyMetrics();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: 2,
      }}
    >
      <Box
        data-pdf-hide
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Select
          name="trendsFilter"
          control={control}
          errors={errors}
          value={filter}
          size="small"
          displayEmpty
          useOnChange={true}
          actionsChange={(value) => {
            setFilter(value);
            setValue("trendsFilter", value, { shouldValidate: false });
          }}
          options={[
            {
              value: "all",
              label: CREATE_CAMPAIGN_AUDIENCE_LOCATION_ALL_OPTION,
            },
            { value: "clicks", label: CLICKS },
            { value: "results", label: RESULTS },
          ]}
          doTranslate={true}
          sx={{ minWidth: 120 }}
          variant="filled"
        />
      </Box>
      <LineChart
        key={`trends-${filter}`}
        pdfCaptureId="overview-trends-card"
        chartConfig={weeklyMetrics.chartConfig}
        chartData={weeklyMetrics.chartData}
        hasCartesianGrid={false}
        hasYAxis={true}
        xAxisKey={weeklyMetrics.xAxisKey}
        title={weeklyMetrics.title}
        subtitle={weeklyMetrics.subtitle}
        valueFormat={{
          translation: VALUE_NUMBER,
          key: "value",
        }}
        sx={{
          width: "100%",
        }}
        height={250}
        yAxisDomain={weeklyMetrics.yAxisDomain}
      />
    </Box>
  );
};

const ComparisonBetweenPlatforms = ({
  statistics,
  filteredLogs,
  isLoadingLogs,
}) => {
  const refs = useResizeItemsWithSameHeight();

  const {
    reportConfiguration,
    watch,
    getCostByMainCurrency,
    getAggregatedMetrics,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const startDateTime = useMemo(
    () => new Date(reportConfiguration?.startDateTime),
    [reportConfiguration?.startDateTime]
  );

  const endDateTime = useMemo(
    () => new Date(reportConfiguration?.endDateTime),
    [reportConfiguration?.endDateTime]
  );

  const breakdown = watch("breakdown");

  const { t } = useTranslationApp();

  const [processedLogs, setProcessedLogs] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const platformFilterValue = watch("platformFilter");

  // Sincronizar el valor inicial con el formulario
  useEffect(() => {
    setValue("platformFilter", "all", { shouldValidate: false });
  }, [setValue]);

  // Sincronizar el estado local con el valor del formulario
  useEffect(() => {
    if (
      platformFilterValue !== undefined &&
      platformFilterValue !== selectedPlatform
    ) {
      setSelectedPlatform(platformFilterValue);
    }
  }, [platformFilterValue, selectedPlatform]);

  useEffect(() => {
    if (!isLoadingLogs && filteredLogs.length > 0) {
      const processedLogsData = filteredLogs
        .map((log) => {
          const title = log.changes?.[0]?.newValue?.title || "";

          return {
            platform: log.platform,
            creationTime: log.creationTime,
            title: title,
          };
        })
        .filter((log) => log.title);

      setProcessedLogs(processedLogsData);
    }
  }, [filteredLogs, isLoadingLogs]);

  const label = getLabelCount(breakdown);

  // Get available platforms from statistics
  const availablePlatforms = useMemo(() => {
    return Object.keys(statistics || {}).filter(
      (key) => key !== "startDateTime" && key !== "endDateTime"
    );
  }, [statistics]);

  const getChartConfigAndDataByMetric = (metric, selectedPlatform = "all") => {
    let chartConfig = {};
    let chartData = [];

    const { current: dataProcessed, previous: previousPeriodData } =
      getAggregatedMetrics({ includePreviousPeriod: true });
    const currentKeys = Object.keys(dataProcessed);
    const previousKeys = previousPeriodData
      ? Object.keys(previousPeriodData)
      : [];
    const maxLength = Math.max(currentKeys.length, previousKeys.length);

    for (let i = 0; i < maxLength; i++) {
      const currentSnapshot = dataProcessed[currentKeys[i]] || {};
      const previousSnapshot = previousPeriodData
        ? previousPeriodData[previousKeys[i]] || {}
        : {};
      const platformsValue = {};
      const platformsValuePrevious = {};
      const platformsGrowth = {};
      let chartConfigLocal = {};
      let chartConfigLocalPrevious = {};

      Object.keys(currentSnapshot).forEach((platform) => {
        if (platform !== "startDateTime" && platform !== "endDateTime") {
          const campaignStats = currentSnapshot[platform]?.campaign;
          const previousCampaignStats = previousSnapshot[platform]?.campaign;

          if (campaignStats) {
            let currentValue, previousValue;
            let rawValue = campaignStats[metric];

            if (METRICS_TO_CONVERT_TO_MAIN_CURRENCY.includes(metric)) {
              currentValue = campaignStats[metric];
              const { cost: currentCost } = getCostByMainCurrency({
                cost: currentValue,
                currency: campaignStats?.currency,
              });
              currentValue = numberDecimal(currentCost);

              if (previousCampaignStats) {
                previousValue = previousCampaignStats[metric];
                const { cost: previousCost } = getCostByMainCurrency({
                  cost: previousValue,
                  currency: previousCampaignStats?.currency,
                });
                previousValue = numberDecimal(previousCost);
              }
            } else {
              currentValue = numberDecimal(campaignStats[metric]);
              if (previousCampaignStats) {
                previousValue = numberDecimal(previousCampaignStats[metric]);
              }
            }

            platformsValue[platform] = currentValue;

            if (previousValue) {
              const growth =
                ((currentValue - previousValue) / previousValue) * 100;
              platformsGrowth[`${platform}Growth`] = growth;
            }

            chartConfigLocal[platform] = {
              label: t(platform),
              color: `hsl(var(--${platform}))`,
              hasLabelList: true,
              isVisible:
                selectedPlatform === "all" || selectedPlatform === platform,
              currency: campaignStats?.currency,
              rawValue: rawValue,
            };
          }
        }
      });

      // Previous period
      Object.keys(previousSnapshot).forEach((platform) => {
        if (platform !== "startDateTime" && platform !== "endDateTime") {
          const campaignStats = previousSnapshot[platform]?.campaign;
          if (campaignStats) {
            if (METRICS_TO_CONVERT_TO_MAIN_CURRENCY.includes(metric)) {
              const value = campaignStats[metric];
              const { cost } = getCostByMainCurrency({
                cost: value,
                currency: campaignStats?.currency,
              });
              platformsValuePrevious[platform + "Previous"] =
                numberDecimal(cost);
            } else {
              platformsValuePrevious[platform + "Previous"] = numberDecimal(
                campaignStats[metric]
              );
            }
            chartConfigLocalPrevious[platform + "Previous"] = {
              label: `${t(platform)} (${t(PREVIOUS_PERIOD)})`,
              color: "transparent",
              hasLabelList: false,
              isVisible:
                selectedPlatform === "all" || selectedPlatform === platform,
            };
          }
        }
      });

      const currentStartDateTime =
        currentSnapshot?.startDateTime || previousSnapshot?.startDateTime;
      const currentEndDateTime =
        currentSnapshot?.endDateTime || previousSnapshot?.endDateTime;

      const dateBasedLabel = getDateBasedLabel(
        breakdown,
        currentStartDateTime,
        currentEndDateTime
      );

      chartData.push({
        label: dateBasedLabel || t(label, { value: i + 1 }),
        breakdown,
        labelToolTip: `${t(DATE_DAY_AND_MONTH_SHORT, {
          date: new Date(
            new Date(currentStartDateTime).getTime() + 24 * 60 * 60 * 1000
          ),
        })} - ${t(DATE_DAY_AND_MONTH_SHORT, {
          date: new Date(currentEndDateTime),
        })}`,
        ...platformsValue,
        ...platformsValuePrevious,
        ...platformsGrowth,
      });

      chartConfig = {
        ...chartConfig,
        ...chartConfigLocal,
        ...chartConfigLocalPrevious,
      };
    }

    const title = t(MAP_STATISTICS_COMPARISON_BY_METRIC[metric]);
    const subtitle = `${t(DATE_DAY_AND_MONTH_SHORT, {
      date: startDateTime,
    })} - ${t(DATE_DAY_AND_MONTH_SHORT, {
      date: endDateTime,
    })}`;

    // Calculate dynamic domain for YAxis when only one platform is visible
    const getYAxisDomain = () => {
      if (selectedPlatform === "all") {
        return undefined; // Use default [0, "dataMax"]
      }

      const values = chartData
        .map((data) => {
          const val = data[selectedPlatform];
          if (val === undefined || val === null) return null;
          const numVal = typeof val === "number" ? val : parseFloat(val);
          return !isNaN(numVal) ? numVal : null;
        })
        .filter((val) => val !== null && val !== undefined && !isNaN(val));

      if (values.length === 0) {
        return undefined;
      }

      const min = Math.min(...values);
      const max = Math.max(...values);

      // If max is 0, return a small range
      if (max === 0) {
        return [0, 1];
      }

      // If all values are the same or very close, add some padding
      if (max === min || (max > 0 && (max - min) / max < 0.01)) {
        const padding = Math.max(max * 0.1, 1);
        return [Math.max(0, min - padding), max + padding];
      }

      const range = max - min;
      const margin = Math.max(range * 0.1, max * 0.05); // At least 5% of max or 10% of range
      const calculatedMin = Math.max(0, min - margin);
      const calculatedMax = max + margin;

      // Return explicit domain to force Recharts to use it
      return [calculatedMin, calculatedMax];
    };

    return {
      chartConfig,
      chartData,
      title,
      subtitle,
      xAxisKey: "label",
      yAxisDomain: getYAxisDomain(),
    };
  };

  const chartCpm = getChartConfigAndDataByMetric("cpm", selectedPlatform);
  const chartCpc = getChartConfigAndDataByMetric("cpc", selectedPlatform);
  const chartCpa = getChartConfigAndDataByMetric(
    "costPerMainAction",
    selectedPlatform
  );

  const renderChart = (chartData, ref, metric, pdfCaptureId) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <LineChart
          key={`${metric}-${selectedPlatform}`}
          ref={ref}
          pdfCaptureId={pdfCaptureId}
          chartConfig={chartData.chartConfig}
          chartData={chartData.chartData}
          hasCartesianGrid={false}
          hasYAxis={true}
          labelKey="label"
          valueKey="value"
          valueFormat={{
            translation: VALUE_NUMBER,
            key: "value",
          }}
          title={chartData.title}
          subtitle={chartData.subtitle}
          height={250}
          hasTooltip={true}
          optimizationLogs={processedLogs}
          yAxisDomain={chartData.yAxisDomain}
          customComponentDetail={
            <DetailComparisonBetweenPlatforms
              statistics={statistics}
              metric={metric}
            />
          }
        />
      </Box>
    );
  };

  const platformOptions = [
    { value: "all", label: CREATE_CAMPAIGN_AUDIENCE_LOCATION_ALL_OPTION },
    ...availablePlatforms.map((platform) => ({
      value: platform,
      label: platform,
    })),
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        data-pdf-hide
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Select
          name="platformFilter"
          control={control}
          errors={errors}
          value={selectedPlatform}
          size="small"
          displayEmpty
          useOnChange={true}
          actionsChange={(value) => {
            setSelectedPlatform(value);
            setValue("platformFilter", value, { shouldValidate: false });
          }}
          options={platformOptions}
          doTranslate={true}
          sx={{ minWidth: 120 }}
          variant="filled"
        />
      </Box>
      <Grid container spacing={3} data-pdf-chart-grid>
        <Grid item {...grids}>
          {renderChart(
            chartCpm,
            (el) => (refs.current[0] = el),
            "cpm",
            "overview-cpm-card"
          )}
        </Grid>
        <Grid item {...grids}>
          {renderChart(
            chartCpc,
            (el) => (refs.current[1] = el),
            "cpc",
            "overview-cpc-card"
          )}
        </Grid>
        <Grid item {...grids}>
          {renderChart(
            chartCpa,
            (el) => (refs.current[2] = el),
            "costPerMainAction",
            "overview-cpa-card"
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const DetailComparisonBetweenPlatforms = ({ statistics, metric }) => {
  const { t } = useTranslationApp();

  const metricValueByMetricCost = {
    cpm: "impressions",
    cpc: "click",
    costPerMainAction: "mainAction",
  };

  const platforms = Object.keys(statistics);

  return (
    <Box>
      <Typography variant="body2" align="right">
        {t(
          MAP_STATISTICS_COMPARISON_BY_METRIC[metricValueByMetricCost[metric]]
        )}
      </Typography>
      {platforms.map((platform) => {
        return (
          <Box
            key={getUniqueID()}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              align="right"
              color="fields.placeholder"
            >
              {`${t(platform)}:`}
            </Typography>
            <Typography variant="caption" align="right">
              {t(VALUE_NUMBER, {
                value:
                  statistics?.[platform].campaign?.[
                    metricValueByMetricCost[metric]
                  ],
              })}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default OverviewStatistics;
